import { ShieldCheckIcon, ShieldAlertIcon } from "lucide-react";

import {
  useAgentMigrationStatus,
  type CredentialType,
} from "@/api/edgeAuth";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { cn, formatDate } from "@/lib/utils";
import { QueryState } from "@/components/QueryState";


function credentialBadge(value?: CredentialType | null) {
  if (!value) return <Badge variant="offline">unknown</Badge>;
  if (value === "jwt") return <Badge variant="online">jwt</Badge>;
  if (value === "mtls") return <Badge variant="online">mtls</Badge>;
  return <Badge variant="warning">token (legacy)</Badge>;
}


export default function EdgeAuthMigration() {
  const { data, isLoading, isError, error, refetch } = useAgentMigrationStatus();

  // T1a — `isLoading || !data` renders "Loading…" FOREVER on a failed fetch:
  // React Query sets isLoading=false and data=undefined, so this branch stays
  // true and nothing ever says the request failed. A spinner that never
  // resolves is the same false signal as an empty table.
  if (isError) {
    return (
      <QueryState
        isLoading={false}
        isError
        error={error}
        label="the edge-auth migration status"
        onRetry={refetch}
      >
        {null}
      </QueryState>
    );
  }
  if (isLoading || !data) return <p className="text-sm">Loading…</p>;
  const { items, summary } = data;
  const safe = summary.safe_to_flip_legacy_off;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          {safe ? (
            <ShieldCheckIcon className="h-6 w-6 text-emerald-600" />
          ) : (
            <ShieldAlertIcon className="h-6 w-6 text-amber-600" />
          )}
          Edge auth migration
        </h1>
        <p className="text-sm text-muted-foreground">
          Watch every recorder agent migrate from the legacy shared
          secret (token) to a per-agent JWT before flipping
          <code className="mx-1">RECORDER_LEGACY_SHARED_SECRET_ACCEPTED=false</code>.
        </p>
      </div>

      <Card>
        <CardContent className="grid grid-cols-2 gap-4 p-4 md:grid-cols-5">
          <Stat label="Agents" value={summary.total} />
          <Stat
            label="Online (≤5 min)"
            value={summary.online}
            tone={summary.online === summary.total ? "good" : "warn"}
          />
          <Stat
            label="Stale"
            value={summary.stale}
            tone={summary.stale === 0 ? "good" : "warn"}
          />
          <Stat
            label="In-sync with declared"
            value={summary.in_sync}
            tone={summary.in_sync === summary.total ? "good" : "warn"}
          />
          <Stat
            label="Safe to flip legacy off"
            value={safe ? "yes" : "not yet"}
            tone={safe ? "good" : "bad"}
          />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Declared</th>
                <th className="px-3 py-2 text-left">Observed</th>
                <th className="px-3 py-2 text-left">In sync</th>
                <th className="px-3 py-2 text-left">Last seen</th>
                <th className="px-3 py-2 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.agent_id} className="border-b last:border-0">
                  <td className="px-3 py-2 font-medium">{row.name}</td>
                  <td className="px-3 py-2">
                    {credentialBadge(row.declared_credential_type)}
                  </td>
                  <td className="px-3 py-2">
                    {credentialBadge(row.auth_method_observed)}
                  </td>
                  <td className="px-3 py-2">
                    {row.in_sync ? (
                      <Badge variant="online">ok</Badge>
                    ) : (
                      <Badge variant="warning">drift</Badge>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {row.last_seen ? formatDate(row.last_seen) : "never"}
                  </td>
                  <td className="px-3 py-2">
                    <Badge variant={row.online ? "online" : "offline"}>
                      {row.online ? "online" : "stale"}
                    </Badge>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="py-6 text-center text-sm text-muted-foreground"
                  >
                    No recorder agents provisioned yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}


function Stat({
  label, value, tone = "default",
}: {
  label: string;
  value: number | string;
  tone?: "default" | "good" | "warn" | "bad";
}) {
  const cls = {
    default: "text-foreground",
    good: "text-emerald-600",
    warn: "text-amber-600",
    bad: "text-red-600",
  }[tone];
  return (
    <div>
      <p className="text-xs uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p className={cn("text-2xl font-semibold", cls)}>{value}</p>
    </div>
  );
}
